/**
 * The HVAC roundup at /compare/ai-receptionist-for-hvac-companies.
 *
 * WHY THIS SHAPE. On 2026-09-20 the 412 archived buyer-question answers under
 * the nightly's runs were mined for their source links. For the HVAC question
 * ("best AI receptionist for HVAC companies after hours emergency calls"),
 * nine of the ten most-cited pages were multi-vendor roundups whose title is
 * the question: one page, several vendors, a table of starting prices, a block
 * per vendor, an FAQ, a date. chleb.ai had no page in that shape. Its /hvac
 * page is a product page and its comparisons are all one against one, so it
 * was absent from the set the engines retrieve from, and it was named in 0 of
 * 17 buyer questions run over run while on-page work found nothing left to
 * ship. This page is that missing shape, for the one trade where the question
 * is measured nightly, so the next run tests it directly.
 *
 * THE STANDARD. Every statement about another company is something that
 * company publishes on the page named in its `sources`, read on `reviewedAt`.
 * Nothing here rates call quality, and nothing is said about a vendor that
 * its own page does not say. `checks` lists the literal strings that were
 * present on each source page that day; scripts/check-roundup-sources.mjs
 * fetches the pages again and reports any string that has gone, which is how
 * a price that changes becomes a flagged row instead of a false sentence.
 * Voksha was considered and left out: its pricing page refuses automated
 * reads, so nothing about it could be verified.
 *
 * COMPETITOR PRICES ARE NEW HERE. Until this page the site published no other
 * company's price. Starting prices are the core of the shape the engines cite,
 * so they are here, dated and sourced, but `startingPrice` is optional on
 * every vendor: delete the field and that row falls back to a link to the
 * vendor's own pricing page, with nothing else to change.
 *
 * Chleb's own figures are literals bound to src/rebuild/commerce.ts PLANS by
 * src/test/hvacRoundup.test.ts. Plain TypeScript with no imports, so
 * scripts/prerender.cjs can transpile it in-process the way it does
 * src/data/webDesignTampa.ts, and the mounted page and the static HTML print
 * one text.
 */
export const HVAC_ROUNDUP_PATH = "/compare/ai-receptionist-for-hvac-companies";

// The line the /comparisons hub prints under its vendor ledger. Both strings
// live here because two things print them, src/rebuild/Comparisons.tsx when the
// page mounts and scripts/prerender.cjs in the document a crawler reads, and
// e2e/comparison-document.spec.ts requires every note on the mounted hub to be
// in that document too. The first version of this link was added to the
// mounted page alone and that test caught it.
export const HVAC_ROUNDUP_HUB_NOTE = {
  lead: "Comparing for one trade?",
  label: "Six AI receptionists for HVAC companies, compared",
} as const;

// Which trade pages have a roundup to point at. One entry today. A second
// trade is a second data module and a second line here, and its trade page
// picks the link up without being edited.
export const ROUNDUP_BY_VERTICAL_SLUG: Record<string, { to: string; text: string }> = {
  hvac: { to: HVAC_ROUNDUP_PATH, text: "Six AI receptionists for HVAC, compared" },
};

export type RoundupSource = { label: string; url: string };
export type RoundupCheck = { url: string; mustContain: string[] };
export type RoundupVendor = {
  id: string;
  name: string;
  /** True for the one row that is the publisher of this page. */
  isPublisher?: boolean;
  /** What the vendor says it is, in its own published words or ours for Chleb. */
  what: string;
  /** Optional on purpose. Remove it and the row links to the vendor's pricing page instead. */
  startingPrice?: string;
  priceNote: string;
  trial: string;
  booking: string;
  hvac: string;
  bestFit: string;
  sources: RoundupSource[];
  /** A one against one page on this site, where one exists. */
  detailHref?: string;
  checks: RoundupCheck[];
};

export const HVAC_ROUNDUP = {
  seo: {
    // Title under 60 characters, description inside 120 to 160, no apostrophe
    // in either: scripts/seo-baseline.cjs reads the meta attribute with a
    // quote-or-apostrophe delimiter.
    title: "AI Receptionists for HVAC Companies Compared | Chleb AI",
    description:
      "Six AI receptionist options for HVAC companies compared on published facts: starting price, trial, calendar booking and what each says about HVAC calls.",
    path: HVAC_ROUNDUP_PATH,
    h1: "AI receptionists for HVAC companies, compared",
    intro:
      "An HVAC company choosing an AI receptionist is choosing who answers a no-heat or no-cool call when the office is closed. This page compares six options on what each one publishes: what it is, where its price starts, how it books, and what it says about HVAC. It is published by Chleb AI, which is one of the six, so the Chleb row uses the same columns as every other row and the page says plainly where another option is the better fit.",
  },
  published: "2026-09-20",
  publishedLabel: "September 20, 2026",
  reviewedAt: "2026-10-09",
  reviewedLabel: "October 9, 2026 (Goodcall); September 20, 2026 (other vendors)",
  disclosure:
    "Published by Chleb AI. Vendor information was read from each company's own published pages on September 20, 2026, with Goodcall rechecked October 9. Every row links its source. Prices and plans change, so confirm them on the vendor's page before you decide. This is a comparison of published scope and price. It is not an independent test of call quality, and no vendor paid to be listed or was asked.",

  criteria: [
    {
      id: "after-hours",
      title: "What happens to an urgent call after hours",
      body: "A no-heat call in January and a tune-up request are different calls. Ask each vendor how its agent tells them apart, what it asks the caller, and who on your team is told, by what channel, while the caller is still reachable.",
    },
    {
      id: "booking",
      title: "Where the booking lands",
      body: "Some options book into Google or Outlook calendars, some name field-service software such as ServiceTitan or Jobber, and some pass details to a person. Ask for the exact path into the tool your dispatcher already uses, and confirm it on a test call before launch.",
    },
    {
      id: "setup",
      title: "Who configures it",
      body: "Self-serve products are set up by you from a dashboard, often the same day. A configured service is set up for you from your services, hours, intake questions and handoff rules. The first costs less and asks for your time. The second costs more and asks for less of it.",
    },
    {
      id: "bill",
      title: "What the whole month costs",
      body: "A starting price is a floor. Minutes, calls, agents, customers and add-ons are billed in different ways by different vendors. Price your own busiest month, not the plan name, and ask what happens when you pass the allowance.",
    },
  ],

  vendors: [
    {
      id: "chleb",
      name: "Chleb AI",
      isPublisher: true,
      what: "An AI receptionist and website chatbot for service businesses, configured by the company's two founders in Tampa, Florida, with a separate CRM and a bundle that joins the two.",
      startingPrice: "$899 a month",
      priceNote:
        "AI Receptionist + Chatbot is $899 a month. The Front Office Bundle, which adds the CRM, is $997 a month. The CRM alone is $297 a month. Usage is billed separately.",
      trial: "No free trial is published. A demo can be booked, and the agent that answers Chleb's own line can be called.",
      booking: "Books into your connected booking calendar. Support for a specific scheduling tool is confirmed on an integration check before launch.",
      hvac: "Asks the triage questions configured for your business, such as whether it is a no-cool or no-heat call, how long the unit has been out, and whether anyone in the home is at risk, then flags urgent calls and sends your on-call tech a summary.",
      bestFit:
        "An HVAC owner who wants the call flow built, tested and handed over rather than assembled from a dashboard, and who wants calls, leads and bookings in one CRM. It has the highest starting price on this page. If you want a self-serve tool under $200 a month, one of the four self-serve options here is the better fit.",
      sources: [
        { label: "Chleb AI pricing", url: "https://chleb.ai/pricing" },
        { label: "Chleb AI for HVAC companies", url: "https://chleb.ai/hvac" },
      ],
      detailHref: "/hvac",
      checks: [],
    },
    {
      id: "rosie",
      name: "Rosie",
      what: "Describes itself as a 24/7 AI answering service that knows your business.",
      startingPrice: "$49 a month",
      priceNote: "Three published plans: Professional at $49 a month, Scale at $149 a month and Growth at $299 a month.",
      trial: "A 7-day free trial is published, with cancel anytime.",
      booking: "States that it books appointments directly on your calendar, and lists a Zapier integration.",
      hvac: "Lists HVAC and home services among its industry pages.",
      bestFit: "An owner who wants the lowest published starting price on this page and is comfortable setting the product up.",
      sources: [{ label: "Rosie pricing", url: "https://heyrosie.com/pricing" }],
      checks: [{ url: "https://heyrosie.com/pricing", mustContain: ["$49", "$149", "$299", "7-day free trial", "Zapier", "industries/hvac"] }],
    },
    {
      id: "goodcall",
      name: "Goodcall",
      what: "Configurable AI phone agents with self-service plans, customer allowances, and an enterprise option.",
      startingPrice: "$79 a month per agent",
      priceNote:
        "Three published monthly plans per agent: Starter at $79 for 100 unique customers, Growth at $129 for 500 and Scale at $299 for 2,000. Extra customers are billed at the plan's own rate: 79¢ on Starter, 26¢ on Growth and 15¢ on Scale.",
      trial: "Goodcall offers a free demo agent; the current FAQ says billing starts when you add a phone number and choose a plan.",
      booking: "Goodcall's current FAQ lists native Google Calendar, Google Sheets and HighLevel connections for GC4; other systems require a configured API tool or MCP server. Zapier is described as a legacy integration for existing GC3 agents. Confirm the account generation, plan and actual write path before you decide.",
      hvac: "This page found no HVAC-specific statement on Goodcall's pricing page. Ask the vendor how it handles an urgent service call.",
      bestFit: "An owner evaluating a configurable AI phone agent who is comfortable pricing by agent and by customer count.",
      sources: [
        { label: "Goodcall pricing", url: "https://www.goodcall.com/pricing" },
        { label: "Goodcall FAQ", url: "https://www.goodcall.com/faq" },
      ],
      detailHref: "/alternatives/goodcall",
      checks: [{ url: "https://www.goodcall.com/pricing", mustContain: ["$79", "$129", "$299", "unique customers", "Get started"] }],
    },
    {
      id: "agentzap",
      name: "AgentZap",
      what: "Describes itself as an AI-powered receptionist that answers calls 24/7, books appointments, and handles customer inquiries for service businesses.",
      startingPrice: "$109 a month",
      priceNote:
        "Published plans: Starter at $109 a month with 150 minutes included, Professional at $295 a month with 450 minutes, and Business at $899 a month with 1,500 minutes, plus custom enterprise pricing.",
      trial: "A 30-day money-back guarantee is published.",
      booking: "States that it books appointments live on the call and works with Google Calendar, Outlook and Calendly.",
      hvac: "Lists HVAC among its industry pages.",
      bestFit: "An owner who wants minute-based plans with a published allowance at each step.",
      sources: [{ label: "AgentZap pricing", url: "https://agentzap.ai/pricing" }],
      checks: [{ url: "https://agentzap.ai/pricing", mustContain: ["$109", "$295", "$899", "150 minutes", "30-day money-back", "ServiceTitan", "Jobber", "Google Calendar", "Calendly", "/industries/hvac"] }],
    },
    {
      id: "nextphone",
      name: "NextPhone",
      what: "Describes itself as an AI receptionist and answering service for law firms and service businesses that answers calls, books appointments and captures every lead.",
      startingPrice: "$199 a month",
      priceNote: "Published plans: Pro at $199 a month, Growth from $299 a month and Scale from $599 a month.",
      trial: "7 days free is published, with cancel anytime.",
      booking: "States that it books into Google and Outlook calendars, and names ServiceTitan among its integrations.",
      hvac: "Publishes a dedicated answering-service page for HVAC companies.",
      bestFit: "An HVAC company that already runs ServiceTitan and wants a self-serve product that names it.",
      sources: [{ label: "NextPhone pricing", url: "https://www.getnextphone.com/pricing" }],
      checks: [{ url: "https://www.getnextphone.com/pricing", mustContain: ["$199", "$299", "$599", "7 days free", "ServiceTitan", "Outlook", "answering-service-for-hvac"] }],
    },
    {
      id: "smith-ai",
      name: "Smith.ai",
      what: "Receptionist services with AI-first, hybrid, and human-first options.",
      priceNote: "Publishes usage-based pricing by the call rather than one monthly figure, so a single starting price would misstate it. See its pricing page for the current tiers.",
      trial: "Confirm current trial or guarantee terms on Smith.ai's own pricing page.",
      booking: "Its published product information covers intake, appointment handling and transfers.",
      hvac: "The option on this page to look at if you want a person involved in some calls, since it publishes hybrid and human-first services alongside AI.",
      bestFit: "A team whose decision turns on how much live-agent involvement it wants in its answering.",
      sources: [
        { label: "Smith.ai AI Receptionist pricing", url: "https://smith.ai/pricing/ai-receptionist" },
        { label: "Smith.ai", url: "https://smith.ai/" },
      ],
      detailHref: "/alternatives/smith-ai",
      checks: [{ url: "https://smith.ai/pricing/ai-receptionist", mustContain: ["/call"] }],
    },
  ] as RoundupVendor[],

  sections: [
    {
      id: "how-to-choose",
      title: "How to choose between them",
      paragraphs: [
        "Start with who will set it up. Four of the six options here publish entry plans from $49 to $199 a month that you configure yourself. If someone on your team has an afternoon to write the greeting, the questions and the after-hours rule, those plans cost far less than a configured service and several offer a trial or a guarantee, so you can test on your own calls before you commit.",
        "Then look at where a booked job has to land. NextPhone names ServiceTitan. AgentZap names Google Calendar, Outlook and Calendly. Rosie lists Zapier. If your dispatcher lives in one tool, make the vendor show you that path on a test call, because a booking that lands in the wrong calendar is a missed job with extra steps.",
        "Last, decide how much of the call you want a person in. Smith.ai publishes hybrid and human-first services next to its AI option. The other five are AI answering, with a handoff to your team.",
      ],
    },
    {
      id: "where-chleb-fits",
      title: "Where Chleb fits, and where it does not",
      paragraphs: [
        "Chleb is the most expensive starting price on this page, and the reason is what is included rather than the software. The company's two founders build each setup from your services, hours, intake questions and handoff rules, and the agent is configured and test-called before your number goes live. The plan covers phone answering and a website chatbot, and the Front Office Bundle adds a CRM so callers become tracked contacts.",
        "That is the right trade for an owner who does not want to build a call flow, and the wrong one for an owner who does. If you want to pay under $200 a month and set it up yourself, choose from the four self-serve options above and use their trials. If you want it built, tested and connected to a CRM, book a demo or call the agent that answers our own phone at (813) 694-8671 and hear it first.",
      ],
    },
    {
      id: "method",
      title: "How this comparison was made",
      paragraphs: [
        "Each vendor row was written from that vendor's own published pages, read on September 20, 2026, except Goodcall, which was updated October 9. Each row links the page it came from. Where a page did not state something, the row says so and tells you what to ask. A vendor whose pricing page could not be read was left out instead of being described from memory.",
        "Nothing here measures how well any agent handles a call, including ours. The only fair test of that is your own calls, which is what the trials and guarantees above are for. If a price or plan on this page no longer matches the vendor's page, the vendor's page is right, and we would like to know so the row can be corrected.",
      ],
    },
  ],

  faqs: [
    {
      question: "What is the cheapest AI receptionist for an HVAC company?",
      answer:
        "Of the six options compared here, Rosie published the lowest starting price at $49 a month when its page was read on September 20, 2026. Goodcall's current pricing page observed October 9, 2026 starts at $79 a month per agent; AgentZap is $109 and NextPhone is $199 in the September 20 comparison snapshot. Chleb AI starts at $899 a month because the setup is built and tested for you. A starting price is a floor, so price your own busiest month before choosing.",
    },
    {
      question: "Which AI receptionist works with ServiceTitan?",
      answer:
        "NextPhone names ServiceTitan among its integrations on its pricing page, and AgentZap lists ServiceTitan and Jobber among its integrations. For any vendor, including Chleb AI, confirm the exact connection to your scheduling tool on a test call before launch, because a named integration does not tell you how a booking lands in your dispatch board.",
    },
    {
      question: "Can an AI receptionist handle after-hours HVAC emergency calls?",
      answer:
        "An AI receptionist can answer at any hour, ask the questions you configure, and alert your on-call tech. Chleb AI asks configured triage questions, such as whether it is a no-cool or no-heat call and whether anyone in the home is at risk, then flags urgent calls and sends the on-call tech a summary. Ask every vendor to show you how its agent separates an urgent call from a routine one, and who is told.",
    },
    {
      question: "Is this comparison independent?",
      answer:
        "No. It is published by Chleb AI, which is one of the six options listed. Every statement about another company comes from that company's own published pages and links its source, and the page says where another option is the better fit. It is a comparison of published scope and price, not a test of call quality.",
    },
  ],
} as const;

export const HVAC_ROUNDUP_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://chleb.ai/" },
        { "@type": "ListItem", position: 2, name: "AI receptionist for HVAC companies", item: "https://chleb.ai/hvac" },
        {
          "@type": "ListItem",
          position: 3,
          name: "AI receptionists for HVAC companies, compared",
          item: "https://chleb.ai/compare/ai-receptionist-for-hvac-companies",
        },
      ],
    },
    {
      "@type": "WebPage",
      "@id": "https://chleb.ai/compare/ai-receptionist-for-hvac-companies#webpage",
      url: "https://chleb.ai/compare/ai-receptionist-for-hvac-companies",
      name: HVAC_ROUNDUP.seo.title,
      description: HVAC_ROUNDUP.seo.description,
      datePublished: HVAC_ROUNDUP.published,
      dateModified: HVAC_ROUNDUP.reviewedAt,
      about: { "@id": "https://chleb.ai/#organization" },
      publisher: { "@id": "https://chleb.ai/#organization" },
      mainEntityOfPage: "https://chleb.ai/compare/ai-receptionist-for-hvac-companies",
    },
    {
      "@type": "ItemList",
      name: "AI receptionist options for HVAC companies",
      numberOfItems: HVAC_ROUNDUP.vendors.length,
      itemListElement: HVAC_ROUNDUP.vendors.map((vendor, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: vendor.name,
        url: vendor.sources[0].url,
      })),
    },
    // In the module on purpose. The Tampa pages take their FAQPage from a
    // subset of src/data/faqs.ts that prerender and the mounted page each
    // rebuild, and e2e holds the two to byte parity. Here both sides
    // serialise this one object, so the parity holds by construction.
    {
      "@type": "FAQPage",
      mainEntity: HVAC_ROUNDUP.faqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
} as const;
